'use client';

import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { Link, useLocation, useNavigationType } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../../utils';
import { Button } from '@knitknot/shared/components/ui/button';
import { interceptPlainClick } from './intercept-plain-click';
import { productText } from '../report/styles';
// The workspace switcher lives in the persistent app shell, entirely outside
// this component's DOM subtree — but it already IS the workspace crumb. The
// shell hands its label down through `AppShellContext` so the trail filter
// below can drop a text crumb that just repeats it, e.g. an entity whose name
// happens to equal the workspace's own ("Intryc / … / Intryc" — visual review
// 2026-07-26). This used to be an `@/hooks/useWorkspaceSwitcher` import, which
// made THE canonical page chrome console-only; see app-shell-context.tsx.
import { useAppShell } from './app-shell-context';

// Per-history-entry scroll positions (keyed by location.key). Module-level so
// the position survives the page component unmounting; bounded + session-only.
const scrollPositions = new Map<string, number>();

export interface BreadcrumbTrailItem {
  label: string;
  to?: string;
  /**
   * Router state to carry on a plain left-click, for a crumb whose ancestor
   * needs context the address cannot hold — the list filters a drill chain is
   * restoring, the claim an answer was opened from.
   *
   * `to` stays the ⌘/middle-click fallback, so the crumb degrades to the bare
   * ancestor rather than to nothing. Added rather than a second crumb component:
   * the canonical Breadcrumb already documents "onClick for state drills, href
   * for routes", and react-router's own `Link` takes `state`, so this is the
   * same contract reaching the page-chrome trail.
   */
  state?: unknown;
}

export interface UnifiedPageLayoutProps {
  /** Primary page title — appears as a large h1. On drill-down pages this is
   *  the section the user came from ("Answers"), and `breadcrumbItems`
   *  renders the remaining trail inline after it ("/ Run"). */
  title?: string;
  /** Optional href for clicking the title (rare — used for drill-up nav). */
  titleLink?: string;
  /** Right-aligned contextual actions on the title row (CTA buttons, kebab menus). */
  actions?: React.ReactNode;
  /** One-sentence page purpose. List and workflow pages should put their job
   *  here instead of repeating the title inside a detached hero card. */
  description?: React.ReactNode;
  /** Contextual navigation owned by this destination. It sits with the page
   *  identity and collapses independently on narrow screens. */
  navigation?: React.ReactNode;
  /** Trail appended inline AFTER the title as "/ X / Y" segments. Each item
   *  with `to` renders as a link; the final item always renders muted-but-
   *  static. Do NOT include the title itself — it's rendered from `title`. */
  breadcrumbItems?: BreadcrumbTrailItem[];
  /** Back button rendered inline with the title (same row).
   *
   *  Pass `href` alongside `onClick` whenever the back target is a KNOWN
   *  address (the list this detail page drilled out of): the control renders as
   *  a real `<a>`, so ⌘/Ctrl-click, middle-click and "Open in new tab" open the
   *  parent list in a new tab, while `onClick` still owns the plain left-click —
   *  which is what keeps the smarter `navigate(-1)`-when-there-is-history
   *  behaviour. `href` is the FALLBACK destination, so it stays correct even
   *  when the click walks history instead. Omit it only for a back whose target
   *  genuinely has no address. */
  backButton?: {
    onClick: () => void;
    label?: string;
    href?: string;
  };
  /** Badges/chips rendered right after the title (status, tier, etc.). */
  titleBadges?: React.ReactNode;
  /** Inline suffix rendered next to the title — e.g. an inline edit control. */
  titleSuffix?: React.ReactNode;
  /** Body content. */
  children: React.ReactNode;
  /** Max-width cap for the content area (default: full). */
  maxWidth?: 'full' | 'reading' | 'dashboard' | 'data' | 'xl' | '2xl';
  /** Extra classes for the outer content card. */
  className?: string;
  /** Extra classes for the inner content wrapper. */
  contentClassName?: string;
  /** Use when the page needs to fill vertical space without the default scroll wrapper (e.g., split layouts). */
  fullHeight?: boolean;
}

/**
 * UnifiedPageLayout — single consistent top section for every page.
 *
 * Stacked rows, top → bottom:
 *   1. Title row: [← back] [title] [/ trail…] [badges] [suffix] … [actions]
 *   2. Purpose sentence (when provided)
 *   3. Contextual navigation (when provided)
 *   4. Content area (scrolls by default, or fullHeight mode for split layouts)
 *
 * Breadcrumbs live INLINE with the title (Settings-style), which keeps the
 * header height identical between list and detail pages.
 */
export const UnifiedPageLayout: React.FC<UnifiedPageLayoutProps> = ({
  title,
  titleLink,
  actions,
  description,
  navigation,
  breadcrumbItems,
  backButton,
  titleBadges,
  titleSuffix,
  children,
  maxWidth = 'full',
  className,
  contentClassName,
  fullHeight = false,
}) => {
  const { key: locationKey } = useLocation();
  const navigationType = useNavigationType();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { workspaceCrumbLabel: shellWorkspaceLabel } = useAppShell();
  const workspaceCrumbLabel = shellWorkspaceLabel?.trim().toLowerCase();

  // Scroll restoration (perf plan §6.3 / Rule 59): a PUSH (forward nav) starts
  // at the top; a POP (back/forward) restores where the user left that history
  // entry — the old unconditional reset was the most frequently FELT defect in
  // daily use (back from a detail page landed at the top of a long list).
  // Positions are keyed by location.key in a module-level map (session-scoped,
  // bounded) and recorded continuously on scroll.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    if (navigationType === 'POP') {
      el.scrollTo(0, scrollPositions.get(locationKey) ?? 0);
    } else {
      el.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationKey]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const record = () => {
      if (scrollPositions.size > 200) scrollPositions.clear();
      scrollPositions.set(locationKey, el.scrollTop);
    };
    el.addEventListener('scroll', record, { passive: true });
    return () => el.removeEventListener('scroll', record);
  }, [locationKey]);

  const maxWidthClass = {
    full: 'max-w-none',
    reading: 'max-w-reading',
    dashboard: 'max-w-dashboard',
    data: 'max-w-none',
    xl: 'max-w-screen-xl',
    '2xl': 'max-w-screen-2xl',
  }[maxWidth];

  // Drop empty segments. Every item renders a leading `/`, so a blank label emits
  // a separator with nothing after it — the trail read "Sources / Coverage /" on
  // `/site/coverage/content/pages` reached without its content-type param (a stale or
  // hand-typed deep link). Filtering here rather than at each call site means no
  // page can render a dangling separator. Mirrors the same guard in the
  // `Breadcrumb` atom.
  // (`label` is typed `string` here, so a blank is `''`/nullish only — the
  // `Breadcrumb` atom additionally guards `false` because its label is a ReactNode.)
  //
  // Also drop a crumb that just repeats the workspace switcher's own label
  // (case-insensitive) — the switcher already IS the workspace crumb, rendered
  // in the persistent shell above this component. Without this, an entity that
  // happens to share the workspace's name (a competitor named the same as the
  // company, e.g. a self-benchmark fixture) renders the same word twice:
  // "[switcher: Intryc ⌄] / Intryc" on the page title row.
  const trail = (breadcrumbItems ?? []).filter((item) => {
    const label = item.label?.trim();
    if (!label) return false;
    if (workspaceCrumbLabel && label.toLowerCase() === workspaceCrumbLabel) return false;
    return true;
  });
  const hasTrail = trail.length > 0;

  const hasTopSection = !!(
    backButton ||
    title ||
    titleBadges ||
    titleSuffix ||
    actions ||
    description ||
    navigation ||
    hasTrail
  );

  const renderTitle = () => {
    if (!title) return null;
    if (titleLink) {
      // `productText.pageTitle`, not the literal `text-xl font-semibold
      // tracking-tight` main carried: that is a re-roll of the canonical token
      // AND a different size (xl vs 2xl). The <Link> is main's — a real href,
      // so the title is ⌘-clickable.
      return (
        <h1 className={cn(productText.pageTitle, 'min-w-0 shrink-0 truncate')}>
          <Link
            to={titleLink}
            state={{ resetToHome: true }}
            className="focus-ring rounded text-inherit no-underline transition-colors hover:text-ink-supporting"
          >
            {title}
          </Link>
        </h1>
      );
    }
    return <h1 className={cn(productText.pageTitle, 'min-w-0 shrink-0 truncate')}>{title}</h1>;
  };

  const renderTrail = () =>
    hasTrail && (
      <nav aria-label="Breadcrumb" className="flex min-h-8 min-w-0 items-center gap-1.5 tracking-tight">
        {trail.map((item, index) => {
          const isLast = index === trail.length - 1;
          const labelClass = cn(productText.crumb, 'truncate max-w-[600px]');
          return (
            <React.Fragment key={`${item.label}-${index}`}>
              <span aria-hidden="true" className={productText.crumbSeparator}>
                /
              </span>
              {item.to && !isLast ? (
                <Link
                  to={item.to}
                  state={item.state}
                  className={cn(labelClass, 'hover:text-ink-supporting transition-colors')}
                >
                  {item.label}
                </Link>
              ) : (
                <span className={labelClass} aria-current={isLast ? 'page' : undefined}>
                  {item.label}
                </span>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    );

  const topSection = hasTopSection ? (
    <div className="flex-shrink-0 border-b border-border bg-card">
      <div className={cn('px-4 py-4 lg:px-6 xl:px-8', navigation && 'pb-0')}>
        <div className="flex min-h-10 w-full min-w-0 flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap">
          {backButton &&
            (backButton.href ? (
              <Button asChild variant="ghost" size="icon" className="h-7 w-7 flex-shrink-0 -ml-1.5 !min-h-0 !min-w-0">
                <a
                  href={backButton.href}
                  onClick={(e) => interceptPlainClick(e, backButton.onClick)}
                  aria-label={backButton.label || 'Go back'}
                >
                  <ArrowLeft className="h-4 w-4" />
                </a>
              </Button>
            ) : (
              <Button
                variant="ghost"
                size="icon"
                onClick={backButton.onClick}
                className="h-7 w-7 flex-shrink-0 -ml-1.5 !min-h-0 !min-w-0"
                aria-label={backButton.label || 'Go back'}
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            ))}
          {renderTitle()}
          {renderTrail()}
          {titleBadges && <div className="flex items-center gap-2 flex-shrink-0">{titleBadges}</div>}
          {titleSuffix && <div className="flex-shrink-0">{titleSuffix}</div>}
          <div className="hidden flex-1 sm:block" />
          {actions && (
            <div className="order-last flex w-full items-center gap-2 pt-1 sm:order-none sm:w-auto sm:flex-shrink-0 sm:justify-end sm:pt-0">
              {actions}
            </div>
          )}
        </div>
        {description && <div className="mt-2 w-full text-sm leading-6 text-ink-supporting">{description}</div>}
        {navigation && <div className="mt-4">{navigation}</div>}
      </div>
    </div>
  ) : null;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className={cn('flex-1 flex flex-col bg-background overflow-hidden', className)}>
        {topSection}

        {fullHeight ? (
          <div className={cn('flex-1 flex flex-col min-h-0', contentClassName)}>{children}</div>
        ) : (
          <div ref={scrollRef} className="flex-1 overflow-y-auto">
            <div className={cn('mx-auto w-full px-4 pb-16 pt-4 lg:px-6 xl:px-8', maxWidthClass, contentClassName)}>
              {children}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Reusable single-label breadcrumb suffix for the `titleSuffix` slot.
 *
 * Prefer the `breadcrumbItems` prop for multi-level trails; use `PageBreadcrumb`
 * only for lightweight inline trail context next to the title (e.g. Settings
 * subpage label).
 */
export function PageBreadcrumb({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-1.5 tracking-tight">
      <span className={productText.crumbSeparator}>/</span>
      <span className={productText.crumb}>{label}</span>
    </span>
  );
}

export default UnifiedPageLayout;
